import React from 'react';
import Plot from 'react-plotly.js';
import { PoolDataHookResult } from '../../types/pool';
import { POOL_CONFIGS, getPoolColor, DEX_INFO } from '../../config/pools';
import { getLineChartLayout, getBaseConfig } from './chartConfig';
import './ChartStyles.css';

interface StackedLiquidityChartProps {
  poolsData: { [key: string]: PoolDataHookResult };
  className?: string;
}

// Helper function to get shorter DEX abbreviations for legends
const getDEXAbbreviation = (dex: string): string => {
  switch (dex) {
    case 'uniswap_v3':
      return 'Uni V3';
    case 'pancakeswap_v3':
      return 'Pancake V3';
    case 'uniswap_v4':
      return 'Uni V4';
    case 'sushiswap_v3':
      return 'Sushi V3';
    default:
      return dex;
  }
};

export const StackedLiquidityChart: React.FC<StackedLiquidityChartProps> = ({ poolsData, className }) => {
  // Prepare data for the chart and calculate master dates
  const { chartData, masterDates } = React.useMemo(() => {
    const traces: any[] = [];
    
    // 1. Collect master set of all dates (normalised to YYYY-MM-DD) - like Python align_dataframes
    const masterDateSet = new Set<string>();
    Object.values(poolsData).forEach((pd) => {
      pd?.snapshots?.forEach((s) => {
        const dateStr = new Date(s.date).toISOString().slice(0, 10); // normalise
        masterDateSet.add(dateStr);
      });
    });
    const masterDates = Array.from(masterDateSet).sort();

    // 2. Build a map of poolKey -> { date -> tvl } - like Python align_dataframes
    const poolSnapshotsMap: Record<string, Record<string, number>> = {};
    Object.entries(poolsData).forEach(([key, pd]) => {
      if (!pd?.snapshots) return;
      poolSnapshotsMap[key] = {};
      pd.snapshots.forEach((s) => {
        const dateStr = new Date(s.date).toISOString().slice(0, 10);
        poolSnapshotsMap[key][dateStr] = s.total_value_locked_usd || 0;
      });
    });

    // 3. Create traces using the aligned masterDates, forward-filling values like Python implementation (ffill)
    POOL_CONFIGS.forEach((poolConfig) => {
      const poolKey = `${poolConfig.dex}_${poolConfig.address}`;
      const dateToValue = poolSnapshotsMap[poolKey] || {};
      const yValues: number[] = [];
      let lastValue = 0;
      masterDates.forEach((d) => {
        const v = dateToValue[d];
        if (v !== undefined) {
          lastValue = v;
        }
        yValues.push(lastValue);
      });

      // Skip pools that have all zeros (no data)
      if (yValues.every((v) => v === 0)) return;

      traces.push({
        x: masterDates,
        y: yValues,
        type: 'scatter',
        mode: 'lines',
        name: `${getDEXAbbreviation(poolConfig.dex)} - ${poolConfig.name}`,
        fill: 'tonexty',
        fillcolor: getPoolColor(poolConfig),
        line: { 
          color: getPoolColor(poolConfig),
          width: 2
        },
        stackgroup: 'one',
        hovertemplate: '%{fullData.name}: $%{y:,.0f}<extra></extra>'
      });
    });
    
    return { chartData: traces, masterDates };
  }, [poolsData]);

  // Calculate default range and available data duration
  const { defaultRange, dataDurationDays } = React.useMemo(() => {
    if (masterDates.length === 0) return { defaultRange: undefined, dataDurationDays: 0 };
    
    const earliestDate = new Date(masterDates[0]);
    const latestDate = new Date(masterDates[masterDates.length - 1]);
    const dataDurationDays = Math.ceil((latestDate.getTime() - earliestDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Default to showing from July onwards if we have enough data, otherwise show all
    const currentYear = latestDate.getFullYear();
    const julyStart = new Date(currentYear, 6, 1); // July 1st of current year
    
    let defaultRange;
    // Only use julyStart if it's actually within our data range (fixes backwards range bug)
    if (dataDurationDays >= 180 && earliestDate <= julyStart && julyStart <= latestDate) {
      // If we have at least 6 months of data and it goes back to July, start from July
      defaultRange = [julyStart.toISOString().split('T')[0], latestDate.toISOString().split('T')[0]];
    } else {
      // Otherwise show all available data
      defaultRange = [earliestDate.toISOString().split('T')[0], latestDate.toISOString().split('T')[0]];
    }
    
    return { defaultRange, dataDurationDays };
  }, [masterDates]);

  // Generate dynamic buttons based on available data
  const rangeSelectorButtons = React.useMemo(() => {
    const buttons: any[] = [];
    
    // Always show 7d if we have at least 7 days
    if (dataDurationDays >= 7) {
      buttons.push({
        count: 7,
        label: '7d',
        step: 'day' as const,
        stepmode: 'backward' as const
      });
    }
    
    // Show 30d if we have at least 30 days
    if (dataDurationDays >= 30) {
      buttons.push({
        count: 30,
        label: '30d',
        step: 'day' as const,
        stepmode: 'backward' as const
      });
    }
    
    // Show 90d if we have at least 90 days
    if (dataDurationDays >= 90) {
      buttons.push({
        count: 90,
        label: '90d',
        step: 'day' as const,
        stepmode: 'backward' as const
      });
    }
    
    // Show 6m if we have at least 6 months (180 days)
    if (dataDurationDays >= 180) {
      buttons.push({
        count: 6,
        label: '6m',
        step: 'month' as const,
        stepmode: 'backward' as const
      });
    }
    
    // Show 1y if we have at least 1 year (365 days)
    if (dataDurationDays >= 365) {
      buttons.push({
        count: 1,
        label: '1y',
        step: 'year' as const,
        stepmode: 'backward' as const
      });
    }
    
    // Always show "All" button if we have any data
    if (dataDurationDays > 0) {
      buttons.push({
        step: 'all' as const,
        label: 'All'
      });
    }
    
    return buttons;
  }, [dataDurationDays]);

  const layout = {
    ...getLineChartLayout('Total Value Locked (TVL) - Historical Trends', 'Date', 'Total Value Locked (USD)'),
    height: 500,
    hovermode: 'x unified' as const, // Use unified hover to show all values at once
    hoverlabel: {
      bgcolor: 'rgba(26, 26, 26, 0.95)',
      bordercolor: '#404040',
      font: { size: 10, color: '#e0e0e0' },
      align: 'left' as const
    },
    // Add Plotly native time frame buttons
    xaxis: {
      ...getLineChartLayout('Total Value Locked (TVL) - Historical Trends', 'Date', 'Total Value Locked (USD)').xaxis,
      type: 'date' as const,
      range: defaultRange, // Default based on available data
      rangeselector: {
        buttons: rangeSelectorButtons,
        bgcolor: 'rgba(26, 26, 26, 0.9)',
        bordercolor: '#404040',
        borderwidth: 1,
        font: {
          color: '#e0e0e0',
          size: 10
        },
        activecolor: '#FF8C00',
        x: 0.0,
        xanchor: 'left' as const,
        y: 1.05, // Position above chart area
        yanchor: 'bottom' as const
      }
    }
  };

  return (
    <div className={`chart-container ${className || ''}`}>
      <h2 className="chart-title ev-accent separator-wide">Total Value Locked (TVL) - Historical Trends</h2>
      <Plot
        data={chartData}
        layout={layout}
        style={{ width: '100%', height: '500px' }}
        config={getBaseConfig()}
      />
    </div>
  );
};
